import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { EventAssignment, ReturnItem } from "@/types/catering";

interface ReturnStockDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: EventAssignment | null;
  ptaId: string | null;
  onReturnComplete: () => void;
  onReturnStock: (stockItemId: string, assignmentId: string, returnQty: number, currentQty: number) => Promise<boolean>;
}

export function ReturnStockDialog({
  open,
  onOpenChange,
  event,
  ptaId,
  onReturnComplete,
  onReturnStock
}: ReturnStockDialogProps) {
  const { toast } = useToast();
  const [returnItems, setReturnItems] = useState<ReturnItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (open && event && ptaId) {
      fetchAssignedItems();
    }
  }, [open, event, ptaId]);

  const fetchAssignedItems = async () => {
    if (!event || !ptaId) return;
    setLoading(true);

    const { data, error } = await supabase
      .from('event_catering_assignments')
      .select(`
        id,
        quantity,
        new_stock_item_id,
        catering_stock_items!event_catering_assignments_new_stock_item_id_fkey (
          id,
          item_name
        )
      `)
      .eq('event_id', event.event_id)
      .eq('pta_id', ptaId)
      .not('new_stock_item_id', 'is', null);

    if (error) {
      console.error('Error fetching assignments:', error);
      setLoading(false);
      return;
    }

    const items: ReturnItem[] = (data || [])
      .filter((a: any) => a.catering_stock_items)
      .map((a: any) => ({
        assignmentId: a.id,
        stockItemId: a.new_stock_item_id,
        itemName: a.catering_stock_items?.item_name || 'Unknown',
        assignedQuantity: a.quantity,
        returnQuantity: 0
      }));

    setReturnItems(items);
    setLoading(false);
  };

  const handleReturnQuantityChange = (assignmentId: string, value: string) => {
    const numValue = parseInt(value) || 0;
    setReturnItems(prev => prev.map(item => 
      item.assignmentId === assignmentId 
        ? { ...item, returnQuantity: Math.min(Math.max(0, numValue), item.assignedQuantity) }
        : item
    ));
  };

  const handleProcessReturns = async () => {
    const itemsToReturn = returnItems.filter(item => item.returnQuantity > 0);
    
    if (itemsToReturn.length === 0) {
      toast({ title: "No Items", description: "Please enter return quantities for at least one item", variant: "destructive" });
      return;
    }

    setProcessing(true);

    let successCount = 0;
    for (const item of itemsToReturn) {
      const success = await onReturnStock(
        item.stockItemId,
        item.assignmentId,
        item.returnQuantity,
        item.assignedQuantity
      );
      if (success) successCount++;
    }

    setProcessing(false);

    if (successCount > 0) {
      onReturnComplete();
      onOpenChange(false);
    }
  };

  const totalReturning = returnItems.reduce((sum, item) => sum + item.returnQuantity, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Return Stock from Event</DialogTitle>
          <DialogDescription>
            {event?.event_name} - Enter the quantity of each item being returned
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-muted-foreground">Loading assigned items...</div>
        ) : returnItems.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            No stock items assigned to this event
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="text-center">Assigned</TableHead>
                  <TableHead className="text-center">Return Qty</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {returnItems.map((item) => (
                  <TableRow key={item.assignmentId}>
                    <TableCell className="font-medium">{item.itemName}</TableCell>
                    <TableCell className="text-center">{item.assignedQuantity}</TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="0"
                        max={item.assignedQuantity}
                        value={item.returnQuantity || ''}
                        onChange={(e) => handleReturnQuantityChange(item.assignmentId, e.target.value)}
                        className="w-24 mx-auto text-center"
                        placeholder="0"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="flex items-center justify-between pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Total items to return: <span className="font-semibold">{totalReturning}</span>
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleProcessReturns} 
                  disabled={processing || totalReturning === 0}
                >
                  {processing ? "Processing..." : "Process Returns"}
                </Button>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              Returned items will be added to the soonest expiring batch of each item.
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
